import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/hooks/useLanguage';
import type { SupportedLanguage } from '@/lib/languageUtils';
import { stripLangPrefix, buildLangPath } from '@/components/LangWrapper';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';

interface LanguageSelectorProps {
  className?: string;
  compact?: boolean;
}

const LANGUAGES = [
  { code: 'en',    flag: 'gb', label: 'English',    short: 'EN' },
  { code: 'pl',    flag: 'pl', label: 'Polski',     short: 'PL' },
  { code: 'pt-br', flag: 'br', label: 'Português (BR)', short: 'PT-BR' },
  { code: 'pt-pt', flag: 'pt', label: 'Português (PT)', short: 'PT-PT' },
  { code: 'es',    flag: 'es', label: 'Español',    short: 'ES' },
  { code: 'de',    flag: 'de', label: 'Deutsch',    short: 'DE' },
  { code: 'hi',    flag: 'in', label: '\u0939\u093F\u0928\u094D\u0926\u0940',       short: 'HI' },
  { code: 'id',    flag: 'id', label: 'Bahasa',     short: 'ID' },
  { code: 'fil',   flag: 'ph', label: 'Tagalog',    short: 'FIL' },
  { code: 'tr',    flag: 'tr', label: 'Türkçe',     short: 'TR' },
  { code: 'ar',    flag: 'sa', label: '\u0627\u0644\u0639\u0631\u0628\u064A\u0629',    short: 'AR' },
  { code: 'vi',    flag: 'vn', label: 'Tiếng Việt', short: 'VI' },
  { code: 'th',    flag: 'th', label: 'ไทย',         short: 'TH' },
  { code: 'ja',    flag: 'jp', label: '日本語',      short: 'JA' },
  { code: 'ko',    flag: 'kr', label: '한국어',      short: 'KO' },
  { code: 'fr',    flag: 'fr', label: 'Français',   short: 'FR' },
  { code: 'it',    flag: 'it', label: 'Italiano',   short: 'IT' },
  { code: 'uk',    flag: 'ua', label: 'Українська', short: 'UK' },
  { code: 'cs',    flag: 'cz', label: 'Čeština',    short: 'CS' },
  { code: 'nl',    flag: 'nl', label: 'Nederlands',  short: 'NL' },
] as const;

// Render a country flag as a flag-icons CSS sprite. Used to render reliably
// across all browsers (Chrome on Windows does not render regional-indicator
// emoji like 🇬🇧 / 🇵🇱 — it falls back to "GB"/"PL" text).
const FlagIcon: React.FC<{ countryCode: string; className?: string }> = ({ countryCode, className = '' }) => (
  <span
    className={`fi fi-${countryCode.toLowerCase()} ${className}`}
    aria-hidden="true"
    style={{ display: 'inline-block', width: '1.33em', height: '1em', backgroundSize: 'cover', backgroundPosition: 'center' }}
  />
);

export const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  className = '',
  compact = false,
}) => {
  const { currentLanguage, changeLanguage } = useLanguage();
  const { t } = useTranslation('common');
  const [isChanging, setIsChanging] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const handleLanguageChange = async (lang: string) => {
    if (lang === currentLanguage) {
      return;
    }

    setIsChanging(true);
    const success = await changeLanguage(lang as SupportedLanguage);
    setIsChanging(false);

    if (!success) {
      console.error('Failed to change language');
      return;
    }

    // Navigate to the language-prefixed URL (or clean URL for English)
    const basePath = stripLangPrefix(location.pathname);
    const targetPath = buildLangPath(basePath, lang);
    if (location.pathname !== targetPath) {
      navigate(targetPath);
    }
  };

  if (isChanging) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <Loader2 className="w-4 h-4 animate-spin" />
        {!compact && <span className="text-sm">...</span>}
      </div>
    );
  }

  const currentLang = LANGUAGES.find(l => l.code === currentLanguage) || LANGUAGES[0];

  return (
    <Select value={currentLanguage} onValueChange={handleLanguageChange} disabled={isChanging}>
      {/* Override the shadcn SelectTrigger base `[&>span]:line-clamp-1`
          which would otherwise truncate "EN"/"PT-BR" to "E..."/"PT..."
          when the header flex row is tight. `min-w-fit` + `shrink-0`
          guarantee the trigger always fits flag + widest short code
          (PT-BR = 5 chars) + chevron without being squeezed by neighbours. */}
      <SelectTrigger
        aria-label={t('select_language')}
        className={`w-auto min-w-fit shrink-0 gap-1.5 px-1.5 sm:px-2 text-[#8B949E] border-none bg-transparent shadow-none hover:text-gray-600 dark:hover:text-gray-300 transition-colors focus:ring-0 focus:ring-offset-0 [&>span]:line-clamp-none ${className}`}
      >
        {/* Flag-icons sprites bleed colour to the edge of their 1.33em box
            (Union Jack white reaches the right edge), so `gap-2`/`gap-3`
            looked visually tighter than the actual pixel value. Use a
            margin-right on the flag span itself plus `gap-2` on the
            parent — combined ~14px optical separation. */}
        <span className="flex items-center whitespace-nowrap">
          <FlagIcon countryCode={currentLang.flag} className="mr-2.5" />
          <span className="hidden sm:inline text-sm font-medium tracking-wide">{currentLang.short}</span>
        </span>
      </SelectTrigger>
      <SelectContent>
        {LANGUAGES.map(lang => (
          <SelectItem key={lang.code} value={lang.code}>
            <span className="flex items-center gap-2">
              <FlagIcon countryCode={lang.flag} />
              <span>{lang.short}</span>
              <span className="text-muted-foreground">{lang.label}</span>
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};
